import axiosInstance from "utils/axios";

/**
 * Get options for a given dropdown enum value
 * @param {number} enumValue - The dropdown enum value
 * @returns {Promise<Object>} Response object with success, data, and optional error
 */
export const getDropdownOptions = async (enumValue) => {
  try {
    const response = await axiosInstance.get("api/Dropdown/Options", {
      params: { enumValue },
      showLoader: false,
    });

    const data = response.data;
    // Handle both direct array and ResponseModel wrapped responses
    const optionsList = data.data !== undefined ? (typeof data.data === "string" ? JSON.parse(data.data) : data.data) : data;

    return {
      success: true,
      data: Array.isArray(optionsList) ? optionsList : [],
    };
  } catch (error) {
    console.error("Error in getDropdownOptions:", error);
    return {
      success: false,
      data: [],
      error: error.message || "Failed to fetch options",
    };
  }
};

/**
 * Create a new option for a given dropdown enum
 * @param {number} enumValue - The dropdown enum value
 * @param {string} optionName - The name of the option
 * @param {number} displayOrder - Optional display order
 * @returns {Promise<Object>} Response object with success, optionId, and message/error
 */
export const addDropdownOption = async (enumValue, optionName, displayOrder = 0) => {
  try {
    const response = await axiosInstance.post("api/Dropdown/Options", {
      enum: enumValue,
      optionName: optionName.trim(),
      displayOrder,
    });

    const result = response.data;
    return {
      success: result.success ?? true,
      optionId: result.optionId,
      message: result.message || "Option added successfully",
    };
  } catch (error) {
    console.error("Error in addDropdownOption:", error);
    return {
      success: false,
      error: error.message || "Failed to add option",
    };
  }
};

/**
 * Update an existing dropdown option
 * @param {string} optionId - The GUID of the option to update
 * @param {string} optionName - The new name of the option
 * @param {number} displayOrder - Optional display order
 * @returns {Promise<Object>} Response object with success and message/error
 */
export const editDropdownOption = async (optionId, optionName, displayOrder = 0) => {
  try {
    const response = await axiosInstance.post(`api/Dropdown/UpdateOptions/${optionId}`, {
      optionName: optionName.trim(),
      displayOrder,
    });

    const result = response.data;
    return {
      success: result.success ?? true,
      message: result.message || "Option updated successfully",
    };
  } catch (error) {
    console.error("Error in editDropdownOption:", error);
    return {
      success: false,
      error: error.message || "Failed to update option",
    };
  }
};

/**
 * Delete a dropdown option (soft delete)
 * @param {string} optionId - The GUID of the option to delete
 * @returns {Promise<Object>} Response object with success and message/error
 */
export const deleteDropdownOption = async (optionId) => {
  try {
    const response = await axiosInstance.post(`api/Dropdown/DeleteOptions/${optionId}`);
    const result = response.data;
    return {
      success: result.success ?? true,
      message: result.message || "Option deleted successfully",
    };
  } catch (error) {
    console.error("Error in deleteDropdownOption:", error);
    return {
      success: false,
      error: error.message || "Failed to delete option",
    };
  }
};
